import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { AssetTypeMaintenanceSchedulesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { AssetTypeMaintenanceScheduleFilters } from '@/types'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { useTable } from '@/helpers/useTable'
import { DeleteDialog } from '../delete_dialog'
import { NewDialog } from './new_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'

interface Props extends TableProps {
  filters?: AssetTypeMaintenanceScheduleFilters
  createable?: boolean
  deleteable?: boolean
}

export const Table = ({ pageSize = 10, filters = {}, createable = false, deleteable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<AssetTypeMaintenanceSchedulesQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const {
    pageInfo,
    openNew,
    setOpenNew,
    openDelete,
    setOpenDelete
  } = useTable({ pageInfo: data?.assetTypeMaintenanceSchedules.pageInfo })
  const [assetTypeMaintenanceScheduleId, setAssetTypeMaintenanceScheduleId] = useState<string>()

  const deleteAssetTypeMaintenanceSchedule = (id: string) => {
    if (!deleteable) return

    setAssetTypeMaintenanceScheduleId(id)
    setOpenDelete(true)
  }

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<AssetTypeMaintenanceScheduleFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Summary' }, { name: '' }]}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.assetTypeMaintenanceSchedules.nodes = dedupe([
              ...previousData.assetTypeMaintenanceSchedules.nodes,
              ...fetchMoreResult.assetTypeMaintenanceSchedules.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        rows={data.assetTypeMaintenanceSchedules.nodes.map(ms => (
          {
            id: ms.id,
            columns: [
              ms.maintenanceSchedule.summary,
              deleteable
                ? <div className="text-right -mx-1">
                  <Button color="red" onClick={() => deleteAssetTypeMaintenanceSchedule(ms.id) }>Delete</Button>
                </div>
                : <></>
            ]
          }
        ))}
      />
      { assetTypeMaintenanceScheduleId && (<DeleteDialog
        id={assetTypeMaintenanceScheduleId}
        mutation={DeleteMutation}
        open={openDelete}
        refetchQueries={[Query]}
        setOpen={setOpenDelete}
        onSuccess={
          () => {
            setOpenDelete(false)
            setAssetTypeMaintenanceScheduleId(undefined)
          }
        }
      />) }
      <NewDialog
        defaultValues={{ assetTypeId: filters?.assetTypeIdEq || undefined }}
        open={openNew}
        setOpen={setOpenNew}
      />
    </>
  )
}
